/**
 * Unix-domain socket server that receives hook envelopes from the hook helper:
 *
 *   - PostToolUse progress envelopes (fire-and-forget) → `tool_progress` events
 *   - PreToolUse requests (`kind: "pre_tool_use"`) → request/response: the
 *     handler's verdict is written back on the SAME connection, correlated by
 *     `id`, so the helper can print a permissionDecision for the CLI.
 *
 * Only newline-delimited JSON envelopes are parsed, and malformed lines are
 * dropped — raw PTY stdout is never parsed here.
 */

import { createServer, type Server, type Socket } from "node:net";
import { unlink } from "node:fs/promises";

export interface ToolProgress {
  tool_name?: string;
  elapsed_time_seconds?: number;
}

export interface PreToolUseRequest {
  id: string;
  tool_name: string;
  tool_input: Record<string, unknown>;
}

export interface PreToolUseVerdict {
  decision: "allow" | "deny";
  reason?: string;
}

export type HookEnvelope =
  | { kind: "progress"; progress: ToolProgress }
  | { kind: "pre_tool_use"; request: PreToolUseRequest };

export function parseEnvelope(line: string): HookEnvelope | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(line);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const record = parsed as Record<string, unknown>;

  if (record.kind === "pre_tool_use") {
    if (typeof record.id !== "string" || typeof record.tool_name !== "string") return null;
    const toolInput =
      typeof record.tool_input === "object" && record.tool_input !== null
        ? (record.tool_input as Record<string, unknown>)
        : {};
    return {
      kind: "pre_tool_use",
      request: { id: record.id, tool_name: record.tool_name, tool_input: toolInput },
    };
  }

  // Legacy progress envelope: any line without a `kind` field.
  const result: ToolProgress = {};
  if (typeof record.tool_name === "string") result.tool_name = record.tool_name;
  if (typeof record.elapsed_time_seconds === "number") {
    result.elapsed_time_seconds = record.elapsed_time_seconds;
  }
  return { kind: "progress", progress: result };
}

export class HookSocketServer {
  private server: Server | null = null;
  private _closed = false;

  constructor(
    public readonly socketPath: string,
    private readonly onProgress: (progress: ToolProgress) => void,
    private readonly onPreToolUse?: (request: PreToolUseRequest) => Promise<PreToolUseVerdict>,
  ) {}

  async listen(): Promise<void> {
    await unlink(this.socketPath).catch(() => undefined);
    await new Promise<void>((resolve, reject) => {
      const server = createServer((socket) => this.handleConnection(socket));
      server.on("error", reject);
      server.listen(this.socketPath, () => {
        resolve();
      });
      this.server = server;
    });
  }

  get isClosed(): boolean {
    return this._closed;
  }

  async close(): Promise<void> {
    if (this._closed) return;
    this._closed = true;
    const server = this.server;
    this.server = null;
    if (server) {
      await new Promise<void>((resolve) => {
        server.close(() => {
          resolve();
        });
      });
    }
    await unlink(this.socketPath).catch(() => undefined);
  }

  private handleConnection(socket: Socket): void {
    // Swallow connection-level errors (e.g. ECONNRESET when the hook helper is
    // killed mid-write during teardown). Without an "error" listener Node
    // treats it as unhandled and crashes the process.
    socket.on("error", () => undefined);
    // Per-connection buffer: concurrent hook helpers each get their own
    // accumulation so interleaved chunks from separate connections can't
    // corrupt one another's lines.
    let buffer = "";
    socket.on("data", (chunk: Buffer) => {
      buffer += chunk.toString("utf8");
      let index = buffer.indexOf("\n");
      while (index >= 0) {
        const line = buffer.slice(0, index);
        buffer = buffer.slice(index + 1);
        this.dispatch(line, socket);
        index = buffer.indexOf("\n");
      }
    });
  }

  private dispatch(line: string, socket: Socket): void {
    const envelope = parseEnvelope(line);
    if (!envelope) return;
    if (envelope.kind === "progress") {
      this.onProgress(envelope.progress);
      return;
    }
    void this.respondToPreToolUse(envelope.request, socket);
  }

  private async respondToPreToolUse(request: PreToolUseRequest, socket: Socket): Promise<void> {
    let verdict: PreToolUseVerdict;
    try {
      verdict = this.onPreToolUse
        ? await this.onPreToolUse(request)
        : { decision: "allow" as const };
    } catch (err) {
      verdict = {
        decision: "deny",
        reason: `Conveyor validation failed: ${err instanceof Error ? err.message : String(err)}. Fix the issue and try again.`,
      };
    }
    try {
      socket.write(`${JSON.stringify({ id: request.id, ...verdict })}\n`);
    } catch {
      /* helper hung up — its own timeout fallback covers this */
    }
  }
}
